import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Order, WaybillBayanStatus, WaybillBlocker } from '@hero/schema';
import { getApiErrorMessage } from '@hero/api';
import { useAppIssueWaybillBayanApi, useAppWaybillBayanStatusApi } from '@hero/api-hooks';
import { AlertCircleIcon, DownloadIcon, OrderWaybillIcon, RefreshIcon } from '@hero/web-icons';
import { useAppConnectorLive, useGovernmentWaybillPdf } from '@hero/web-hooks';
import { LOGISTI_APP } from '@hero/apps';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../../../ui/Badge';
import { Button } from '../../../../ui/Button';
import { Collapse } from '../../../../app/Collapse';
import { DetailRow } from './DetailRow';

export interface GovernmentWaybillDetailsProps {
  order: Order;
}

/**
 * The Bayan (Logisti) government waybill — وثيقة النقل — for one order.
 *
 * ## Why this renders nothing when the Org isn't connected
 *
 * The overwhelming majority of Orgs have no Bayan account. A permanently
 * empty "Government waybill" panel on every order would train people to
 * ignore that region of the drawer, which is exactly where a real blocker
 * needs to be noticed. So the panel appears only once the connector is live
 * for a party to the order.
 *
 * ## Why blockers are listed rather than summarised.
 *
 * A blocker is always a specific missing field on a specific driver, truck,
 * or Org record, and fixing it is a two-minute job for whoever is looking at
 * this drawer. "Waybill failed" sends them to support; "Driver ID issue
 * number" sends them to the driver's profile.
 */
export const GovernmentWaybillDetails: React.FC<GovernmentWaybillDetailsProps> = ({ order }) => {
  const { t } = useTranslation(['m-apps']);
  const orderId = String(order._id);
  const isLogistiLive = useAppConnectorLive(LOGISTI_APP.id);

  // Gating the query as well as the render keeps the drawer from asking about
  // a waybill on every order open while the connector is switched off.
  const { data: waybill, isPending } = useAppWaybillBayanStatusApi(orderId, {
    enabled: isLogistiLive,
  });

  const issue = useAppIssueWaybillBayanApi({
    onSuccess: result => {
      if (result.status === 'issued') {
        toast.success(t('m-apps:toast.success.waybill_issued'));
        return;
      }
      // Reaching here means the call was accepted but produced no document —
      // a blocker or a provider rejection. The panel below shows the detail,
      // so an error toast on top of it would just be noise.
      toast.error(result.lastError ?? t('m-apps:toast.error.waybill_failed'));
    },
    onError: (error: Error) =>
      toast.error(error.message || t('m-apps:toast.error.waybill_failed')),
  });

  // Shared with the actions menu and the ref chip so all three open the
  // document the same way — in a tab, from the archived URL when there is one.
  // This panel used to force a download instead, which made the government
  // waybill the only document in the product that behaved differently from
  // Hero's own.
  const pdf = useGovernmentWaybillPdf(order, {
    pdfUrl: waybill?.pdfUrl,
    onError: () => toast.error(t('m-apps:toast.error.download_failed')),
  });

  if (!isLogistiLive || isPending || !waybill?.connected) return null;

  const status = waybill.status;
  const hasBlockers = waybill.blockers.length > 0;
  const canIssue = !status || status === 'pending' || status === 'failed';
  const isIssued = status === 'issued' || status === 'closed';

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={t('m-apps:waybill.title')}
        icon={
          <div className="p-1.5 bg-emerald-50 rounded-lg">
            <OrderWaybillIcon className="size-5 text-emerald-600" />
          </div>
        }
        defaultOpen={hasBlockers || status === 'failed'}
        action={<StatusBadge status={status} />}>
        <div className="space-y-0">
          {waybill.tripId ? (
            <DetailRow
              icon={<OrderWaybillIcon className="size-5 text-gray-900" />}
              label={t('m-apps:waybill.trip_id')}
              value={String(waybill.tripId)}
            />
          ) : null}

          {waybill.waybillIds.length > 0 ? (
            <DetailRow
              icon={<OrderWaybillIcon className="size-5 text-gray-900" />}
              label={t('m-apps:waybill.waybill_number')}
              value={waybill.waybillIds.join(', ')}
            />
          ) : null}

          {waybill.issuingOrgName ? (
            <DetailRow
              icon={<OrderWaybillIcon className="size-5 text-gray-900" />}
              label={t('m-apps:waybill.issued_by')}
              value={waybill.issuingOrgName}
              showDivider={hasBlockers || canIssue || isIssued}
            />
          ) : null}

          {!status || (status === 'pending' && !hasBlockers) ? (
            <p className="py-3 text-[13px] leading-[20px] text-gray-500">
              {t('m-apps:waybill.empty_description')}
            </p>
          ) : null}

          {hasBlockers ? <BlockerList blockers={waybill.blockers} /> : null}

          {!hasBlockers && waybill.lastError ? (
            <p className="mt-3 rounded-lg bg-red-50 p-3 text-[13px] leading-[20px] text-red-700">
              {waybill.lastError}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-4">
            {canIssue ? (
              <Button
                variant="outline"
                size="md"
                disabled={hasBlockers || issue.isPending}
                onClick={() => issue.mutate({ orderId })}>
                {issue.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshIcon className="size-4" />
                )}
                {status === 'failed'
                  ? t('m-apps:waybill.retry')
                  : t('m-apps:waybill.issue_now')}
              </Button>
            ) : null}

            {isIssued ? (
              <Button
                variant="outline"
                size="md"
                disabled={pdf.isPending}
                onClick={() => pdf.open()}>
                {pdf.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <DownloadIcon className="size-4" />
                )}
                {t('m-apps:waybill.download')}
              </Button>
            ) : null}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/**
 * Blockers, each keyed to a translated field name.
 *
 * Falls back to the backend's own message when a code has no translation. New
 * blocker codes get added on the backend as Bayan's validation surface is
 * mapped, and an untranslated-but-readable line beats a blank row.
 */
const BlockerList: React.FC<{ blockers: WaybillBlocker[] }> = ({ blockers }) => {
  const { t } = useTranslation(['m-apps']);

  return (
    <div className="mt-3 rounded-lg bg-amber-50 p-3">
      <div className="flex items-center gap-2">
        <AlertCircleIcon className="size-4 text-amber-600" />
        <span className="text-[13px] font-[600] leading-[20px] text-amber-900">
          {t('m-apps:waybill.blockers_title')}
        </span>
      </div>
      <p className="mt-0.5 ps-6 text-[12px] leading-[18px] text-amber-700">
        {t('m-apps:waybill.blockers_description')}
      </p>
      <ul className="mt-2 space-y-1 ps-6">
        {blockers.map(blocker => (
          <li
            key={`${blocker.entity}-${blocker.code}`}
            className="text-[13px] leading-[20px] text-amber-900 before:me-2 before:content-['•']">
            {getApiErrorMessage(blocker.code) ?? blocker.message}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TONE: Record<WaybillBayanStatus | 'none', string> = {
  none: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
  issued: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-blue-100 text-blue-700',
  canceled: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

const StatusBadge: React.FC<{ status: WaybillBayanStatus | undefined }> = ({ status }) => {
  const { t } = useTranslation(['m-apps']);
  const key = status ?? 'pending';

  return (
    <Badge variant="secondary" size="small" className={TONE[status ?? 'none']}>
      {t(`m-apps:waybill.status.${key}`)}
    </Badge>
  );
};